import { BuyerUI } from '@choco-admin/types';
import {
  Accordion,
  AccordionItem,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Snippet,
  Spinner,
} from '@heroui/react';
import React from 'react';
import { FiAlertTriangle, FiCheck } from 'react-icons/fi';

import { useGoogleMapsReady, useGooglePlaceDetails } from '../../hooks';
import {
  COORDINATE_DRIFT_THRESHOLD_METRES,
  distanceBetween,
  formatUtcOffset,
} from '../../utils';

import { BuyerLocationMap } from './BuyerLocationMap';

interface LocationDataPanelProps {
  buyer: BuyerUI;
}

/** Reference ids are for copying into a ticket, not for reading. */
const PlaceIdRow: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-3 py-1">
    <p className="shrink-0 text-xs text-default-500">{label}</p>
    {value ? (
      <Snippet
        size="sm"
        variant="flat"
        symbol=""
        codeString={value}
        classNames={{ pre: 'max-w-[13rem] truncate font-mono text-xs' }}
      >
        {value}
      </Snippet>
    ) : (
      <p className="text-xs text-default-400">—</p>
    )}
  </div>
);

/**
 * Where this buyer sits, and whether Google still agrees.
 *
 * Shows the answer rather than a field dump: the pin, one line of detail, then a
 * single verdict against Google — with the per-field comparison only when
 * something actually differs. A retired or repointed place id is the drift worth
 * catching, and coordinates are the field that matters most: without them the
 * backend leaves the buyer out of its geo index entirely.
 */
export const LocationDataPanel: React.FC<LocationDataPanelProps> = ({
  buyer,
}) => {
  const mapsReadiness = useGoogleMapsReady();
  // The business id is the richer record; the address id is the fallback for
  // custom buyers, which never have a business id.
  const lookupPlaceId = buyer.googlePlaceId || buyer.googlePlaceAddressId;
  const { place, isLoading, isUnavailable } =
    useGooglePlaceDetails(lookupPlaceId);

  // One narrowed pair rather than two nullable fields: it keeps the map props
  // honest without casting.
  const coordinates =
    typeof buyer.latitude === 'number' && typeof buyer.longitude === 'number'
      ? { latitude: buyer.latitude, longitude: buyer.longitude }
      : undefined;

  const distance =
    coordinates &&
    typeof place?.latitude === 'number' &&
    typeof place?.longitude === 'number'
      ? distanceBetween(coordinates, {
          latitude: place.latitude,
          longitude: place.longitude,
        })
      : undefined;

  const positionDiffers =
    typeof distance === 'number' &&
    distance > COORDINATE_DRIFT_THRESHOLD_METRES;
  const addressDiffers = Boolean(
    place?.formattedAddress &&
    buyer.fullAddress &&
    place.formattedAddress !== buyer.fullAddress
  );
  const utcOffsetDiffers =
    typeof place?.utcOffsetMinutes === 'number' &&
    typeof buyer.utcOffset === 'number' &&
    place.utcOffsetMinutes !== buyer.utcOffset;
  const differs = positionDiffers || addressDiffers || utcOffsetDiffers;

  // Deliberately NOT part of the comparison: a buyer's name is chosen by CS and
  // routinely differs from the Google place name, so treating that as drift
  // would flag almost every buyer. It is shown as information instead.
  const googleName =
    place?.name && place.name !== buyer.name ? place.name : undefined;

  return (
    <Card className="mt-6" data-testid="location-data-panel">
      <CardHeader className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Location</h3>
          <p className="text-xs text-default-500">
            This buyer&apos;s location, checked against Google.
          </p>
        </div>
        <Chip
          size="sm"
          variant="flat"
          color={coordinates ? 'success' : 'warning'}
          data-testid="location-data-status"
        >
          {coordinates ? 'Located' : 'No coordinates'}
        </Chip>
      </CardHeader>

      <CardBody className="gap-4 pt-0">
        {coordinates && mapsReadiness === 'ready' && (
          <BuyerLocationMap
            latitude={coordinates.latitude}
            longitude={coordinates.longitude}
            title={buyer.name || 'Buyer location'}
          />
        )}
        {coordinates && mapsReadiness === 'loading' && (
          <div className="flex h-56 items-center justify-center rounded-lg border border-default-200 bg-default-100">
            <Spinner size="sm" />
          </div>
        )}
        {coordinates && mapsReadiness === 'unavailable' && (
          <div className="flex h-20 items-center justify-center rounded-lg border border-default-200 bg-default-100 px-4 text-center">
            <p className="text-xs text-default-500">
              Map unavailable — the Google Maps key is not authorised for this
              domain.
            </p>
          </div>
        )}

        <div>
          <p className="text-sm text-foreground">{buyer.fullAddress || '—'}</p>
          <p className="mt-0.5 text-xs text-default-500">
            {coordinates
              ? `${coordinates.latitude}, ${coordinates.longitude}`
              : 'No coordinates'}
            {typeof buyer.utcOffset === 'number' &&
              ` · ${formatUtcOffset(buyer.utcOffset)}`}
          </p>
        </div>

        {lookupPlaceId && isLoading && (
          <div className="flex items-center gap-2 text-xs text-default-500">
            <Spinner size="sm" />
            Checking against Google…
          </div>
        )}

        {lookupPlaceId && !isLoading && isUnavailable && (
          <p className="text-xs text-default-500">
            Google has no record for this buyer&apos;s reference — it may have
            been retired or merged into another place.
          </p>
        )}

        {place && !differs && (
          <div
            className="flex items-center gap-2 text-xs text-success-600"
            data-testid="location-google-match"
          >
            <FiCheck size={14} />
            Address, position and time zone match Google
          </div>
        )}

        {place && differs && (
          <div
            className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-2"
            data-testid="location-google-diff"
          >
            <p className="flex items-center gap-2 text-sm font-medium text-foreground">
              <FiAlertTriangle size={14} />
              Google has different details
            </p>
            <div className="mt-1 space-y-0.5 text-xs text-default-700">
              {addressDiffers && <p>Google: {place.formattedAddress}</p>}
              {positionDiffers && (
                <p>Google&apos;s pin is {distance} m from this one.</p>
              )}
              {utcOffsetDiffers && place?.utcOffsetMinutes !== undefined && (
                <p>
                  Google&apos;s time zone is{' '}
                  {formatUtcOffset(place.utcOffsetMinutes)}.
                </p>
              )}
            </div>
          </div>
        )}

        {googleName && (
          <p
            className="text-xs text-default-500"
            data-testid="google-name-note"
          >
            Google lists this place as &ldquo;{googleName}&rdquo;.
          </p>
        )}

        <Accordion
          isCompact
          className="px-0"
          itemClasses={{ trigger: 'py-2', title: 'text-xs text-default-500' }}
        >
          <AccordionItem
            key="ids"
            aria-label="Google reference ids"
            title="Google reference ids"
          >
            <PlaceIdRow label="Business" value={buyer.googlePlaceId} />
            <PlaceIdRow label="Address" value={buyer.googlePlaceAddressId} />
          </AccordionItem>
        </Accordion>
      </CardBody>
    </Card>
  );
};
